import { z } from 'zod';
import { geoFilterShape } from './geography.js';
import { dateRangeQuerySchema, paginationQuerySchema, queryBooleanSchema } from './pagination.js';
import { anomalyReasonSchema, ATTACHMENT_STATUSES, ATTACHMENT_VERIFICATIONS, VISIT_STATUSES } from './visits.js';
export const dashboardFilterSchema = z
    .object({
    agentId: z.uuid().optional(),
    brandId: z.uuid().optional(),
    branchId: z.uuid().optional(),
    ...geoFilterShape,
})
    .and(dateRangeQuerySchema);
export const kpiResponseSchema = z.object({
    totals: z.object({
        visits: z.number().int(),
        synced: z.number().int(),
        partiallySynced: z.number().int(),
        failed: z.number().int(),
        inProgress: z.number().int(),
        anomalies: z.number().int(),
        avgDurationMinutes: z.number().nullable(),
        /** synced / (synced + partiallySynced + failed); null when nothing finished. */
        syncSuccessRate: z.number().nullable(),
        anomalyRate: z.number().nullable(),
    }),
    byAgent: z.array(z.object({
        userId: z.string(),
        fullName: z.string(),
        visits: z.number().int(),
        anomalies: z.number().int(),
        avgDurationMinutes: z.number().nullable(),
    })),
    byBranch: z.array(z.object({
        branchId: z.string().nullable(),
        branchName: z.string(),
        brandName: z.string().nullable(),
        visits: z.number().int(),
        anomalies: z.number().int(),
    })),
    byDay: z.array(z.object({ date: z.string(), visits: z.number().int(), anomalies: z.number().int() })),
});
const coordinates = z.object({ latitude: z.number(), longitude: z.number() }).nullable();
export const anomalyListQuerySchema = paginationQuerySchema
    .extend({
    agentId: z.uuid().optional(),
    branchId: z.uuid().optional(),
    ...geoFilterShape,
    minScore: z.coerce.number().int().min(0).optional(),
})
    .and(dateRangeQuerySchema);
export const anomalyItemSchema = z.object({
    visitId: z.string(),
    submissionId: z.string(),
    agent: z.object({ id: z.string(), fullName: z.string(), phoneNumber: z.string() }),
    brandName: z.string().nullable(),
    branchName: z.string().nullable(),
    isUnlistedBranch: z.boolean(),
    startedAt: z.string(),
    endedAt: z.string(),
    durationMinutes: z.number(),
    anomalyScore: z.number().int(),
    reasons: z.array(anomalyReasonSchema),
    startLocation: coordinates,
    endLocation: coordinates,
    branchLocation: coordinates,
    distanceStartMeters: z.number().nullable(),
    distanceEndMeters: z.number().nullable(),
    zohoTicketNumber: z.string().nullable(),
    status: z.enum(VISIT_STATUSES),
});
export const syncIssuesQuerySchema = paginationQuerySchema.extend({
    /** UNCONFIRMED_PHOTOS: a photo Zoho gave an id for was not confirmed on the ticket by the read-back. */
    status: z.enum(['FAILED', 'PARTIALLY_SYNCED', 'STUCK', 'UNCONFIRMED_PHOTOS']).optional(),
});
export const syncIssueItemSchema = z.object({
    visitId: z.string(),
    submissionId: z.string(),
    status: z.enum(VISIT_STATUSES),
    agent: z.object({ id: z.string(), fullName: z.string() }),
    branchName: z.string().nullable(),
    zohoTicketNumber: z.string().nullable(),
    syncAttempts: z.number().int(),
    lastError: z.string().nullable(),
    failedAttachments: z.number().int(),
    pendingAttachmentUploads: z.number().int(),
    /**
     * Photos declared on the visit, and those that reached Zoho (attachment status SYNCED).
     * Optional for backward compatibility: servers before 2026-09-17 do not send them (D38).
     */
    totalAttachments: z.number().int().optional(),
    uploadedAttachments: z.number().int().optional(),
    /**
     * Photos whose read-back from the Zoho ticket did not confirm them (not listed,
     * wrong size, or the check could not run). Optional for older servers.
     */
    unconfirmedAttachments: z
        .array(z.object({
        photoId: z.string(),
        fileName: z.string(),
        status: z.enum(ATTACHMENT_STATUSES),
        verification: z.enum(ATTACHMENT_VERIFICATIONS),
        detail: z.string().nullable(),
    }))
        .optional(),
    receivedAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// Trips and project visits (visits table, export, issue numbers)
// ---------------------------------------------------------------------------
const optionKey = z.string().trim().min(1).max(100);
/** Filters shared by the visits table, its export and the issue numbers. Read from our database only. */
const projectVisitFilterShape = {
    agentId: z.uuid().optional(),
    brandId: z.uuid().optional(),
    branchId: z.uuid().optional(),
    projectKey: optionKey.optional(),
    serviceKey: optionKey.optional(),
    issueTypeKey: optionKey.optional(),
    /** One specific issue (an option key of the Issues field). */
    issueKey: optionKey.optional(),
    issueFound: queryBooleanSchema.optional(),
    ...geoFilterShape,
};
export const projectVisitFilterSchema = z.object(projectVisitFilterShape).and(dateRangeQuerySchema);
export const issueStatsQuerySchema = projectVisitFilterSchema;
export const projectVisitListQuerySchema = paginationQuerySchema.extend(projectVisitFilterShape).and(dateRangeQuerySchema);
/** A stored option key with its labels from the form version the visit used (the key itself when the option is gone). */
export const optionRefSchema = z.object({ key: z.string(), label: z.string(), labelEn: z.string().nullable() });
/** Any answer shown as text, with its field's labels. */
export const answerSchema = z.object({
    fieldKey: z.string(),
    label: z.string(),
    labelEn: z.string().nullable(),
    /** Arabic display text (option labels, Ù†Ø¹Ù…/Ù„Ø§); null when not answered. */
    value: z.string().nullable(),
    valueEn: z.string().nullable(),
});
export const blockProjectSchema = z.object({ key: z.string().nullable(), name: z.string().nullable(), typed: z.boolean() });
const projectColumns = {
    project: blockProjectSchema.nullable(),
    visitType: optionRefSchema.nullable(),
    service: optionRefSchema.nullable(),
    serviceDetail: z.array(optionRefSchema),
    /** Null for visits recorded before project blocks existed. */
    issueFound: z.boolean().nullable(),
    issueType: optionRefSchema.nullable(),
    issues: z.array(optionRefSchema),
    comment: z.string().nullable(),
};
/**
 * One row per project visit. The trip reference and every branch-level column
 * repeat on each row of the same trip, so the export can be pivoted.
 */
export const projectVisitRowSchema = z.object({
    rowId: z.string(),
    visitId: z.string(),
    submissionId: z.string(),
    /** The Zoho ticket number when there is one, else the start of the submission id. */
    tripRef: z.string(),
    /** 0 = the main visit's project, n = additional project visit n. */
    position: z.number().int(),
    projectCount: z.number().int(),
    startedAt: z.string(),
    endedAt: z.string(),
    durationMinutes: z.number(),
    agent: z.object({ id: z.string(), fullName: z.string() }),
    brandName: z.string().nullable(),
    branchName: z.string().nullable(),
    /** The branch's area and zone now (D81); null for a branch typed as "not in the list". */
    areaName: z.string().nullable(),
    zoneName: z.string().nullable(),
    isUnlistedBranch: z.boolean(),
    /** The other branch-level answers (branch manager, phone...). */
    branchFields: z.array(answerSchema),
    ...projectColumns,
    photoCount: z.number().int(),
    status: z.enum(VISIT_STATUSES),
    zohoTicketNumber: z.string().nullable(),
    isAnomaly: z.boolean(),
});
export const tripPhotoDtoSchema = z.object({
    photoId: z.string(),
    fieldKey: z.string(),
    fileName: z.string(),
    status: z.enum(ATTACHMENT_STATUSES),
    verification: z.enum(ATTACHMENT_VERIFICATIONS).nullable(),
    zohoAttachmentId: z.string().nullable(),
});
export const tripProjectSchema = z.object({
    position: z.number().int(),
    ...projectColumns,
    /** Every per-project answer, in form order (generic: new fields appear without code changes). */
    answers: z.array(answerSchema),
    photos: z.array(tripPhotoDtoSchema),
});
export const tripDetailSchema = z.object({
    visitId: z.string(),
    submissionId: z.string(),
    tripRef: z.string(),
    formVersion: z.number().int(),
    status: z.enum(VISIT_STATUSES),
    zohoTicketNumber: z.string().nullable(),
    lastSyncError: z.string().nullable(),
    startedAt: z.string(),
    endedAt: z.string(),
    durationMinutes: z.number(),
    agent: z.object({ id: z.string(), fullName: z.string(), phoneNumber: z.string() }),
    brandName: z.string().nullable(),
    branchName: z.string().nullable(),
    isUnlistedBranch: z.boolean(),
    isAnomaly: z.boolean(),
    anomalyScore: z.number().int(),
    branchFields: z.array(answerSchema),
    projects: z.array(tripProjectSchema),
    /** Photos not tied to a project block (visits recorded before project blocks existed). */
    otherPhotos: z.array(tripPhotoDtoSchema),
});
const countRow = optionRefSchema.extend({ count: z.number().int() });
/** Issue numbers per branch, service, issue type, issue and day, over project visits. */
export const issueStatsResponseSchema = z.object({
    totals: z.object({
        projectVisits: z.number().int(),
        withIssue: z.number().int(),
        /** withIssue / projectVisits; null when there are none. */
        issueRate: z.number().nullable(),
    }),
    byService: z.array(optionRefSchema.extend({ projectVisits: z.number().int(), withIssue: z.number().int() })),
    byIssueType: z.array(countRow),
    byIssue: z.array(countRow),
    byBranch: z.array(z.object({
        branchId: z.string().nullable(),
        branchName: z.string(),
        brandName: z.string().nullable(),
        projectVisits: z.number().int(),
        withIssue: z.number().int(),
    })),
    byDay: z.array(z.object({ date: z.string(), projectVisits: z.number().int(), withIssue: z.number().int() })),
    /** Filter choices: every option key seen in the answer's field of any form version, with its newest labels. */
    options: z.object({
        projects: z.array(optionRefSchema),
        services: z.array(optionRefSchema),
        issueTypes: z.array(optionRefSchema),
        issues: z.array(optionRefSchema),
    }),
});
//# sourceMappingURL=dashboard.js.map